<div class="multiselect-container" [class.multiselect-container--disabled]="disabled()">
  <div class="multiselect-search">
    <neo-form-field>
      @if (!hideLabel()) {
        <neo-label>{{ label() }}</neo-label>
      }
      <neo-chip-grid
        #chipGrid
        [attr.aria-label]="label()"
        [attr.aut-id]="autId() + '-chip-grid'"
      >
        <!-- Individual chips (when alwaysShowIndividualChips is true or single selection) -->
        @if (alwaysShowIndividualChips() || selectedItems().length === 1) {
          @for (itemVm of selectedItemVms(); track itemVm.key) {
            <neo-chip-row (removed)="onChipRemoved(itemVm.item)">
              {{ itemVm.label }}
              <button
                neoChipRemove
                [attr.aria-label]="'Remove ' + itemVm.label"
                [attr.aut-id]="autId() + '-chip-remove-' + itemVm.key"
              >
                <cm-icon name="cancel" size="xs" />
              </button>
            </neo-chip-row>
          }
        }

        <!-- Multiple chips with count (when alwaysShowIndividualChips is false) -->
        @else if (selectedItems().length > 1) {
          <neo-chip-row (removed)="onAllChipsRemoved()">
            {{ selectedItems().length }} selected
            <button
              neoChipRemove
              aria-label="Clear all selections"
              [attr.aut-id]="autId() + '-chip-remove-all'"
            >
              <cm-icon name="cancel" size="xs" />
            </button>
          </neo-chip-row>
        }

        <!-- Search input -->
        <input
          #chipInput
          class="multiselect-search-input"
          [placeholder]="currentPlaceholder()"
          [attr.aria-label]="label()"
          [attr.aria-describedby]="hint() ? autId() + '-hint' : null"
          [ngModel]="searchValue()"
          (ngModelChange)="onSearchChange($event)"
          (blur)="onInputBlur()"
          [ngModelOptions]="{ standalone: true }"
          [neoChipInputFor]="chipGrid"
          [neoAutocomplete]="autoComplete"
          [neoChipInputSeparatorKeyCodes]="separatorKeys()"
          [useAsMultiselect]="true"
          [disabled]="disabled()"
          [attr.aut-id]="autId() + '-search-input'"
        />

        <!-- Autocomplete dropdown -->
        <neo-autocomplete-v2
          #autoComplete="neoAutocomplete"
          [multiple]="true"
          [class]="'multiselect-autocomplete-panel'"
          (optionSelected)="onOptionSelected($event)"
          (opened)="onPanelOpened()"
          (closed)="onPanelClosed()"
        >
          @if (isPaginatedMode() && paginationConfig()?.enableVirtualScroll) {
            <!-- Paginated mode with virtual scroll -->
            <!-- CRITICAL: Virtual scroll requires flat list - groups not supported -->
            <!-- cdkScrollable makes dropdown reposition correctly when scrolling -->
            <cdk-virtual-scroll-viewport
              #virtualScrollViewport
              cdkScrollable
              [itemSize]="paginationConfig()!.itemHeight"
              [style.height.px]="calculateViewportHeight()"
              (scrolledIndexChange)="handleLoadMore($event)"
              class="multiselect-virtual-scroll"
            >
              <!-- Flat options only - grouping incompatible with virtual scroll -->
              <neo-option
                *cdkVirtualFor="let vm of virtualScrollOptionVms; trackBy: trackVmForVirtualScroll"
                [value]="vm.item"
                [manualSelected]="vm.isVisuallySelected"
                [class.cm-multiselect-option-indeterminate]="vm.isIndeterminate"
                [disabled]="vm.isDisabled"
              >
                {{ vm.displayText }}
                <!-- Disabled reason shown as persistent visible text (not a native title tooltip) so it's available without hover/focus — WCAG 1.4.13 / 1.3.1. -->
                @if (vm.isDisabled && vm.disabledReason) {
                  <span neoOptionSubtext>{{ vm.disabledReason }}</span>
                } @else if (vm.subtext !== null) {
                  <span neoOptionSubtext>{{ vm.subtext }}</span>
                }
              </neo-option>
            </cdk-virtual-scroll-viewport>

            <!-- Loading indicator OUTSIDE viewport - prevents height calculation issues -->
            @if (loadingMore()) {
              <div class="multiselect-loading">
                <span>Loading more...</span>
              </div>
            }
          } @else {
            <!-- Static mode or paginated without virtual scroll -->
            @if (hasGroups()) {
              <!-- Grouped options -->
              @for (group of groupOptionVms(); track group.label) {
                <neo-optgroup [label]="group.label">
                  @for (vm of group.itemVms; track vm.trackKey) {
                    <neo-option
                      [value]="vm.item"
                      [manualSelected]="vm.isVisuallySelected"
                      [class.cm-multiselect-option-indeterminate]="vm.isIndeterminate"
                      [disabled]="vm.isDisabled"
                    >
                      {{ vm.displayText }}
                      <!-- Disabled reason shown as persistent visible text (not a native title tooltip) so it's available without hover/focus — WCAG 1.4.13 / 1.3.1. -->
                      @if (vm.isDisabled && vm.disabledReason) {
                        <span neoOptionSubtext>{{ vm.disabledReason }}</span>
                      } @else if (vm.subtext !== null) {
                        <span neoOptionSubtext>{{ vm.subtext }}</span>
                      }
                    </neo-option>
                  }
                </neo-optgroup>
              }
            } @else {
              <!-- Flat options -->
              @for (vm of flatOptionVms(); track vm.trackKey) {
                <neo-option
                  [value]="vm.item"
                  [manualSelected]="vm.isVisuallySelected"
                  [class.cm-multiselect-option-indeterminate]="vm.isIndeterminate"
                  [disabled]="vm.isDisabled"
                >
                  {{ vm.displayText }}
                  <!-- Disabled reason shown as persistent visible text (not a native title tooltip) so it's available without hover/focus — WCAG 1.4.13 / 1.3.1. -->
                  @if (vm.isDisabled && vm.disabledReason) {
                    <span neoOptionSubtext>{{ vm.disabledReason }}</span>
                  } @else if (vm.subtext !== null) {
                    <span neoOptionSubtext>{{ vm.subtext }}</span>
                  }
                </neo-option>
              }
            }
          }
        </neo-autocomplete-v2>
      </neo-chip-grid>

      <!-- Error messages via reusable component -->
      <cm-field-errors
        [control]="ngControl()?.control || null"
        [label]="label()"
        [customMessages]="errorMessages()"
        [showErrorsWhen]="showErrorsWhen()"
        [hideErrors]="hideErrors()"
        [constraints]="{
          maxlength: null,
          minlength: null,
          min: null,
          max: null
        }"
      />

      <!-- Legacy ng-content projection for backward compatibility -->
      <ng-content />
    </neo-form-field>

    @if (hint()) {
      <neo-hint [id]="autId() + '-hint'">{{ hint() }}</neo-hint>
    }
  </div>

  <!-- Action button (optional) -->
  @if (showButton()) {
    <cm-button
      class="multiselect-button"
      [color]="buttonColor()"
      [disabled]="buttonDisabled()"
      [loading]="buttonLoading()"
      [tooltip]="buttonTooltip()"
      [autId]="autId() + '-button'"
      (click)="onButtonClicked()"
    >
      @if (buttonIcon()) {
        <cm-icon [name]="buttonIcon()" size="md" />
      }
      {{ buttonLabel() }}
    </cm-button>
  }
</div>
